<script setup lang="ts">
import { uuid } from 'vue3-uuid'
import { Icon } from '@iconify/vue'
import './attachment_list.scss'
import { computed, ref, toRaw, watch } from 'vue'
import * as docxPreview from 'docx-preview'
import VuePdfKit from 'vue-pdf-kit'
import 'vue-pdf-kit/dist/style.css'
import { APIClient } from '@/onenetwork/core/onenetwork.api.instant.ts'
import { UPLOAD_API } from '@/onenetwork/constants/onenetwork.api.constant.ts'
import { useToast } from 'primevue/usetoast'

interface Props {
  data: object,
  editable?: boolean,
  folder?: string
}
const toast = useToast()
const props = defineProps<Props>()
const displayPreview = ref(false)
const pdfPreview = ref(null)
const previewContainer = ref<HTMLElement>(null)
const inputFileRef = ref<HTMLInputElement | null>(null)
const imageRef = ref()
const previewTitle = ref('')
const previewImgSrc = ref('')
const emit = defineEmits(['remove', 'update'])

const editable = computed(() => {
  return props.editable
})

const uploadFolder = computed(() => {
  return props.folder || 'common'
})

const parseData = (data: any) => {
  if(data && data.length > 0){
    return data.map(item => {
      const _item = item
      if(_item.type == 'document'){
        if(item.mime_type.includes('application/vnd.openxmlformats')) _item.type = 'doc'
        if(item.mime_type.includes('application/pdf')) _item.type = 'pdf'
      }
      if(_item.type == 'photo'){
        if(item.mime_type.includes('image/jpeg')) _item.type = 'jpg'
        if(item.mime_type.includes('image/png')) _item.type = 'png'
      }
      return _item;
    })
  }
  return []
}

const dataList = ref(parseData(props.data))
const uploadingIds = ref<any>([])
const removeIds = ref<any>([])

const handlePreviewFile = async (item: any) => {
  console.log(item);

  if (item.type === 'doc') {
    const res = await fetch(item.url.original)
    const arrayBuffer = await res.arrayBuffer()
    displayPreview.value = true
    previewTitle.value = item.original_filename
    docxPreview.renderAsync(arrayBuffer, previewContainer.value).then(()=>{

    })
  }
  if(item.type === 'pdf') {
    displayPreview.value = true
    previewTitle.value = item.original_filename
    pdfPreview.value = item.url.original
  }
  if(['jpg', 'png'].includes(item?.type) || item?.mime_type?.includes('image/')) {
    console.log(item.url.original);
    previewImgSrc.value = item.url.original
    imageRef.value?.$el.querySelector('button')?.click()
  }
}

const getUploadFileType = (mimeType: string) => {
  if(mimeType.includes('application/vnd.openxmlformats')) return 'document'
  if(mimeType.includes('application/pdf')) return 'document'
  if(mimeType.includes('image/jpeg')) return 'photo'
  if(mimeType.includes('image/png')) return 'photo'
}

const handleClosePreview = () => {
  displayPreview.value = false
  previewTitle.value = ''
  if(previewContainer.value) {
    previewContainer.value.innerHTML   = ''
    pdfPreview.value = null
  }
}

const handleRemoveItem = (item: any) => {
  if (!editable.value) return;

  dataList.value.splice(dataList.value.indexOf(item), 1)
  removeIds.value = [...toRaw(removeIds.value), item.uuid]
  // Gửi event ra cha để xoá item tại source gốc
  emit('remove', item)
  emit('update', {attachmentIds: toRaw(dataList.value), removeIds: toRaw(removeIds.value) })

  // Optionally: đóng preview nếu đang preview chính item đó
  if (
    previewTitle.value === item.original_filename ||
    previewImgSrc.value === item.url?.original ||
    pdfPreview.value === item.url?.original
  ) {
    handleClosePreview()
  }
}

const handleSelectFile = (e: any) => {
  if (inputFileRef.value) {
    (inputFileRef.value as HTMLInputElement).click();
  }
}

const putLoadingId = (id: string) => {
  uploadingIds.value = [...toRaw(uploadingIds.value), id]
}

const removeLoadingId = (id: string) => {
  uploadingIds.value = uploadingIds.value.filter((item:string) => item !== id)
}

const handleChangeFile = async (e: any) => {
  if (inputFileRef.value) {
    const _uuid = uuid.v4().toString()
    const file = e.target.files[0];

    putLoadingId(_uuid)
    if(!file) return false
    try{
      const formData = new FormData()
      formData.append('file',file)
      formData.append('uuid', _uuid)
      formData.append('type', getUploadFileType(file.type))
      formData.append('folder', uploadFolder.value)

      const res = await APIClient.post(UPLOAD_API, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      })
      dataList.value.push(res.data.data)
      dataList.value = parseData(toRaw(dataList.value))

      emit('update', {attachmentIds: toRaw(dataList.value), removeIds: toRaw(removeIds.value) })
      toast.add({ severity: 'success', summary: 'Upload thành công!', detail: "File đã được upload thành công!", life: 5000});
    } catch (e: any) {

      const msg = e.response?.data?.errors[0]?.error || "--"
      toast.add({ severity: 'error', summary: 'Upload thất bại', detail: msg, life: 5000});
    } finally {
      e.target.value = ''
      removeLoadingId(_uuid)
    }
  }
}

watch(()=>uploadingIds, ()=>{

})

watch(()=>props.data, (newVal, oldVal) => {
  console.log(newVal, oldVal);

  dataList.value = parseData(toRaw(newVal))
})
</script>

<template>
  <ul class="attachment_list">
    <li class="attachment_list__item" v-for="(item, idx) in dataList" :key="item.id || item.original_filename || idx">
      <div :class="`icon ${item.type}`" v-if="item.type && ['doc', 'docx'].includes(item.type)"><Icon icon="bxs:file-doc"/></div>
      <div :class="`icon ${item.type}`" v-if="item.type && ['pdf'].includes(item.type)"><Icon icon="bxs:file-pdf"/></div>
      <div :class="`icon ${item.type}`" v-if="item.type && ['png', 'jpg', 'jpeg'].includes(item.type)"><Icon :icon="`bxs:file-${item.type}`"/></div>
      <div class="link" @click="()=>handlePreviewFile(item)">{{item.original_filename}}</div>
      <div class="size" v-if="item.file_size">{{Math.round(item.file_size / 1000)}}KB</div>
      <div class="remove" v-if="editable" @click="()=>handleRemoveItem(item)">
        <div class="icon">
          <Icon icon="material-symbols:close" />
        </div>
        Xoá
      </div>
    </li>
    <li class="uploader" v-if="uploadingIds.length > 0">
      <div class="loader">
        <ProgressBar mode="indeterminate" style="height: 6px"></ProgressBar>
      </div>
    </li>
    <li v-if="editable" class="new_file" @click="handleSelectFile"><Icon icon="mage:file-plus" /> <span>Thêm tệp mới</span></li>
  </ul>
  <div style="display: none">
    <input type="file" ref="inputFileRef" @change="handleChangeFile"/>
  </div>
  <div :class="`preview_doc_container ${displayPreview ? 'show' : ''}`">
    <div class="preview_header">
      <h3>{{previewTitle}}</h3>
      <button type="button" @click="handleClosePreview">Đóng</button>
    </div>
    <div class="preview_doc">
      <div ref="previewContainer"></div>
      <div v-if="pdfPreview">
        <vue-pdf-kit :scale="1" :source="pdfPreview"/>
      </div>
    </div>
  </div>
  <div style="display: none">
    <Image
      ref="imageRef"
      preview
      alt="Image"
      :src="previewImgSrc"
    />
  </div>

</template>

<style scoped>

</style>
